import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { Box, Button, Card, Stack, Tab, Tabs, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import { useQueryClient } from '@tanstack/react-query';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PageContainer from '../../components/Redesing/page-container';
import CustomToast from '../../components/Redesing/custom-toast';
import { useChat, Conversation } from '../../hooks/useChat';
import { useAdminStore } from '../../store/adminStore';
import { SUPPORT_CHAT_UNREAD_KEY } from '../../hooks/chat/useSupportChatUnread';
import { useAgentHeartbeat } from '../../hooks/chat/useAgentHeartbeat';
import { ConversationList, StatusFilter } from './ConversationList';
import { ConversationView } from './ConversationView';
import { SendOptions, useComposerDrafts } from './Composer';
import { SupportStats } from './SupportStats';
import { NotificationBanner } from './NotificationBanner';
import { supportTitle, waitingSince } from './sla';
import { SHOW_ASSISTANT } from './flags';
import { useChatTagGlossary } from './tagGlossary';
import { useWriteAccess } from './writeAccess';
import { holderLabel, isHeldByOther, isUnclaimed } from './assignee';
import { takeOverNoteText } from './transferNote';
import { paths, supportChatPath } from '../../paths';

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

/** Remembered per browser: the status tab the agent last had open. */
export const STATUS_FILTER_KEY = 'supportChat.statusFilter';

const STATUS_FILTERS: StatusFilter[] = ['all', 'open', 'in_progress', 'closed'];

/** The remembered status tab, or "all": an unknown or hidden value is not honoured. */
const storedStatusFilter = (): StatusFilter => {
  try {
    const stored = window.localStorage.getItem(STATUS_FILTER_KEY);
    if (stored === 'assistant') return SHOW_ASSISTANT ? 'assistant' : 'all';
    return STATUS_FILTERS.find((f) => f === stored) ?? 'all';
  } catch (_) {
    return 'all';
  }
};

/** A key pressed here is the agent writing, not a shortcut. */
const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[contenteditable=""], [contenteditable="true"]'));
};

/** A dialog, menu or select is open: its own keys, not the page's. */
const isOverlayOpen = (target: EventTarget | null): boolean => {
  if (document.querySelector('[role="dialog"]')) return true;
  return (
    target instanceof HTMLElement &&
    Boolean(target.closest('.MuiPopover-root, .MuiModal-root, [role="menu"]'))
  );
};

const rememberStatusFilter = (filter: StatusFilter) => {
  try {
    window.localStorage.setItem(STATUS_FILTER_KEY, filter);
  } catch (_) {
    // Private mode: the tab starts on All next visit.
  }
};

export default function SupportPage() {
  const admin = useAdminStore.getState().admin;
  const adminEmail = admin?.admin?.email || '';
  const adminName =
    `${admin?.admin?.firstName || ''} ${admin?.admin?.lastName || ''}`.trim() ||
    adminEmail;

  // The URL names the open conversation (/support/chat/:sessionId): a link
  // from Slack or Copy link lands on it, a refresh keeps it, /support is the
  // queue. Opening one from the queue is a step the browser's Back undoes;
  // only a redirect (a link that failed) replaces the entry.
  const { sessionId } = useParams<{ sessionId?: string }>();
  const history = useHistory();
  const showInUrl = useCallback(
    (path: string) => {
      if (history.location.pathname !== path) history.push(path);
    },
    [history]
  );

  const [tab, setTab] = useState<'conversations' | 'stats'>('conversations');
  // Global page: ant scopes the queue by the token; the list filters by system.
  const chat = useChat(adminEmail, adminName, {
    // A desktop notification's click lands on the chat the rider wrote in,
    // also from the Stats tab (the URL alone would not leave it when the
    // chat is the one already open).
    onOpen: (conv) => {
      setTab('conversations');
      showInUrl(supportChatPath(conv.id));
    },
  });
  // "Agents online" in the rider's chat header counts this page, while visible.
  useAgentHeartbeat(Boolean(adminEmail));
  // CS's tag glossary, once per page load: the pickers and every tag label.
  const { glossary, error: glossaryError } = useChatTagGlossary();
  const {
    conversations,
    closedConversations,
    assistantConversations,
    loadClosed,
    totalUnread,
    unreadByConv,
    watchAssistant,
  } = chat;
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(storedStatusFilter);
  // Only the tab the agent clicks is remembered: a link to a closed session
  // puts the page under Closed for that visit, not for the next ones.
  const pickStatusFilter = useCallback((filter: StatusFilter) => {
    setStatusFilter(filter);
    rememberStatusFilter(filter);
  }, []);
  // What the agent was writing in the chats they are not looking at.
  const drafts = useComposerDrafts();
  // Every write goes through here: the session ant refuses one on (an admin
  // whose role reads the chat and no more) is read-only from then on.
  const { canWrite, guard } = useWriteAccess();
  const writes = useMemo(
    () => ({
      sendMessage: guard(chat.sendMessage),
      assign: guard(chat.assignConversation),
      addTag: guard(chat.addTag),
      removeTag: guard(chat.removeTag),
      setPriority: guard(chat.setPriority),
      close: guard(chat.closeConversation),
      takeOver: guard(chat.takeOver),
    }),
    [
      guard,
      chat.sendMessage,
      chat.assignConversation,
      chat.addTag,
      chat.removeTag,
      chat.setPriority,
      chat.closeConversation,
      chat.takeOver,
    ]
  );
  // The keyboard: the rows in the list's order, the row j/k are on, and
  // how many times `e` asked to end the open conversation.
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [cursorId, setCursorId] = useState<string | undefined>(undefined);
  const [endRequested, setEndRequested] = useState(0);

  // The live copy of the selected session: assignment, tags and priority
  // updates show at once, and a session that closes moves to the closed list
  // and stays on screen, read-only.
  const { withRider } = chat;
  const liveSelected = useMemo(() => {
    if (!selected) return null;
    return (
      conversations.find((c) => c.id === selected.id) ??
      closedConversations.find((c) => c.id === selected.id) ??
      assistantConversations.find((c) => c.id === selected.id) ??
      // An earlier session opened from the panel: in no list, named here.
      withRider(selected)
    );
  }, [selected, conversations, closedConversations, assistantConversations, withRider]);

  // The assistant's sessions are read while their filter is on screen.
  useEffect(() => {
    watchAssistant(statusFilter === 'assistant');
  }, [statusFilter, watchAssistant]);

  useEffect(() => {
    if (statusFilter !== 'closed') return;
    loadClosed().catch((error) =>
      CustomToast({
        type: 'error',
        message: `Could not load closed conversations. ${errorText(error)}`,
        duration: 6000,
      })
    );
  }, [statusFilter, loadClosed]);

  // The sidebar badge counts conversations with unread rider messages; this
  // page knows it live, so it writes it where the sidebar reads it.
  const queryClient = useQueryClient();
  const unreadConversations = useMemo(
    () => Object.values(unreadByConv).filter((n) => n > 0).length,
    [unreadByConv]
  );
  useEffect(() => {
    if (chat.queueLoaded) {
      queryClient.setQueryData(SUPPORT_CHAT_UNREAD_KEY, unreadConversations);
    }
  }, [queryClient, unreadConversations, chat.queueLoaded]);

  const { arrivedWhileHidden } = chat;
  useEffect(() => {
    document.title = supportTitle(totalUnread, arrivedWhileHidden);
    return () => {
      document.title = 'Support Chat';
    };
  }, [totalUnread, arrivedWhileHidden]);

  const { joinConversation, loadMessages, markAsRead } = chat;
  const unreadRef = useRef(unreadByConv);
  unreadRef.current = unreadByConv;
  const canMarkReadRef = useRef(canWrite);
  canMarkReadRef.current = canWrite;
  // The session the URL last opened, so the queue's polls do not open it again.
  const openedRef = useRef<string | null>(null);
  const openGenerationRef = useRef(0);
  useEffect(() => {
    let pathname = history.location.pathname;
    // Listen synchronously: a promise can settle before route effects clean up.
    const unlisten = history.listen((location) => {
      if (location.pathname === pathname) return;
      pathname = location.pathname;
      openGenerationRef.current += 1;
      if (!openedRef.current || pathname !== supportChatPath(openedRef.current)) {
        openedRef.current = null;
      }
    });
    return () => {
      unlisten();
      openGenerationRef.current += 1;
      openedRef.current = null;
    };
  }, [history]);
  const handleSelect = useCallback(
    (conv: Conversation) => {
      setSelected(conv);
      setCursorId(conv.id);
      setTab('conversations');
      openedRef.current = conv.id;
      showInUrl(supportChatPath(conv.id));
    },
    [showInUrl]
  );

  // Only a visible thread is watched. Leaving it also cancels its pending
  // read, including a slow first load that answers after Escape, Back or Stats.
  useEffect(() => {
    if (!selected || tab !== 'conversations') {
      joinConversation(null);
      return;
    }
    const conv = selected;
    let cancelled = false;
    joinConversation(conv.id);
    loadMessages(conv.id)
      .then(() => {
        // ant's read mark is per rider, not per session: reading a closed
        // session (Closed filter, Earlier conversations) must not clear the
        // rider's open one. Only an open session with unread rider messages.
        // An assistant session is only watched: the rider's "Seen" is not Support's.
        // Marking read is a write too: not for a session ant refused them on.
        if (
          !cancelled &&
          conv.status !== 'closed' &&
          conv.handled_by !== 'assistant' &&
          (unreadRef.current[conv.id] ?? 0) > 0 &&
          canMarkReadRef.current(conv.id)
        ) {
          markAsRead(conv.id);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        CustomToast({
          type: 'error',
          message: `Could not load the conversation. ${errorText(error)}`,
          duration: 6000,
        });
      });
    return () => {
      cancelled = true;
      joinConversation(null);
    };
  }, [selected, tab, joinConversation, loadMessages, markAsRead]);

  const deselect = useCallback(() => {
    setSelected(null);
    openedRef.current = null;
    showInUrl(paths.support);
  }, [showInUrl]);

  // The keyboard, anywhere on the page but while writing or in a dialog:
  // j/k walk the queue, Enter/o open, n opens the next rider waiting, a
  // claims the open chat when nobody has it (claimChat), e ends it (unless
  // another agent has it), Esc closes it.
  const findConversation = useCallback(
    (id: string | undefined): Conversation | undefined =>
      id
        ? conversations.find((c) => c.id === id) ??
          closedConversations.find((c) => c.id === id) ??
          assistantConversations.find((c) => c.id === id)
        : undefined,
    [conversations, closedConversations, assistantConversations]
  );
  // The lists as they are now, for work that ends renders after it began.
  const findConversationRef = useRef(findConversation);
  findConversationRef.current = findConversation;
  const { myAdminId, refreshSession } = chat;
  const { assign: assignConversation } = writes;
  /**
   * Makes a chat nobody has, as far as the list knows, this agent's: the
   * first reply's claim, `a`, and the panel's Assign to me. ant takes any
   * assign, and the list can be behind it (an event on its way, or up to a
   * poll while the socket is down), so ant is asked who has the chat first:
   * a colleague who took it keeps it, the list shows them, and this answers
   * why it did not assign. Resolves null once the chat is this agent's.
   * Two claims inside one round trip can still both land, the last one
   * winning: only an "assign if nobody has it" in ant closes that.
   */
  const claimChat = useCallback(
    async (convId: string, adminId: string): Promise<string | null> => {
      const now = await refreshSession(convId);
      if (!now || isUnclaimed(now)) {
        await assignConversation(convId, adminId);
        return null;
      }
      if (now.assigned_admin_id === adminId) return null;
      if (now.status === 'closed') return 'The chat has ended.';
      if (now.assigned_admin_id) {
        return `${holderLabel(now.assigned_admin_name)} has this chat now.`;
      }
      return 'The Assistant is answering this chat.';
    },
    [refreshSession, assignConversation]
  );
  useEffect(() => {
    if (tab !== 'conversations') return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey)
        return;
      if (isTypingTarget(event.target) || isOverlayOpen(event.target)) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      const inList = Boolean(target?.closest('[role="option"]'));
      // The arrows scroll whatever else has focus (the thread, the page):
      // they walk the queue only from within it; j/k do from anywhere.
      const inListbox = Boolean(target?.closest('[role="listbox"], [role="option"]'));
      // Enter on a focused button, link or tab activates it (that is the
      // key's default there): the page must not take it and open a chat.
      const onControl = Boolean(
        target?.closest('button, a, [role="button"], [role="tab"], [role="menuitem"]')
      );
      const at = cursorId ?? liveSelected?.id;
      const index = at ? orderedIds.indexOf(at) : -1;
      const moveTo = (next: number) => {
        if (orderedIds.length === 0) return;
        const bounded = Math.max(0, Math.min(orderedIds.length - 1, next));
        setCursorId(orderedIds[bounded]);
        event.preventDefault();
      };
      switch (event.key) {
        case 'ArrowDown':
          if (inListbox) moveTo(index + 1);
          return;
        case 'ArrowUp':
          if (inListbox) moveTo(index <= 0 ? 0 : index - 1);
          return;
        case 'j':
          moveTo(index + 1);
          return;
        case 'k':
          moveTo(index <= 0 ? 0 : index - 1);
          return;
        case 'Enter':
        case 'o': {
          // A focused row opens itself on Enter (it is a button), as does
          // any other control the agent tabbed to.
          if (event.key === 'Enter' && (inList || onControl)) return;
          const conv = findConversation(cursorId ?? orderedIds[0]);
          if (conv && conv.id !== liveSelected?.id) handleSelect(conv);
          event.preventDefault();
          return;
        }
        case 'n': {
          // The next rider waiting after the open chat, round to the top.
          const from = liveSelected ? orderedIds.indexOf(liveSelected.id) : -1;
          const rotated = [
            ...orderedIds.slice(from + 1),
            ...orderedIds.slice(0, from + 1),
          ];
          const next = rotated
            .map(findConversation)
            .find((c) => c && c.id !== liveSelected?.id && waitingSince(c) !== null);
          if (next) handleSelect(next);
          event.preventDefault();
          return;
        }
        case 'a':
          // Only a chat nobody has: one another agent has is taken over from
          // the composer, with a note saying so, never by a stray key.
          if (
            liveSelected &&
            myAdminId &&
            liveSelected.status !== 'closed' &&
            !liveSelected.assigned_admin_id &&
            canWrite(liveSelected.id)
          ) {
            const refuse = (why: string) =>
              CustomToast({
                type: 'error',
                message: `Could not assign the conversation. ${why}`,
                duration: 6000,
              });
            claimChat(liveSelected.id, myAdminId)
              .then((why) => {
                if (why) refuse(why);
              })
              .catch((error) => refuse(errorText(error)));
            event.preventDefault();
          }
          return;
        case 'e':
          if (
            liveSelected &&
            liveSelected.status !== 'closed' &&
            !isHeldByOther(liveSelected, myAdminId) &&
            canWrite(liveSelected.id)
          ) {
            setEndRequested((n) => n + 1);
            event.preventDefault();
          }
          return;
        case 'Escape':
          if (liveSelected) {
            deselect();
            event.preventDefault();
          }
          return;
        default:
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    tab,
    cursorId,
    orderedIds,
    liveSelected,
    findConversation,
    handleSelect,
    deselect,
    claimChat,
    myAdminId,
    canWrite,
  ]);

  // The URL stopped naming a conversation (the sidebar's Support Chat link,
  // the browser's Back from the first chat opened): the thread closes with it.
  useEffect(() => {
    if (!sessionId && selected) deselect();
  }, [sessionId, selected, deselect]);

  // The conversation the URL names: from a list the page holds once the queue
  // is in (so unread counts are too), else fetched from ant with its own
  // system and status, and put under the filter that lists it. A session
  // the token may not read, or that is gone, sends the agent back to the queue.
  const { openSession, queueLoaded } = chat;
  useEffect(() => {
    if (!sessionId || !queueLoaded || openedRef.current === sessionId) return;
    openedRef.current = sessionId;
    const known = conversations.find((c) => c.id === sessionId);
    if (known) {
      handleSelect(known);
      return;
    }
    // Under the filter that lists it, as a fetched session is.
    const assistant = assistantConversations.find((c) => c.id === sessionId);
    if (assistant) {
      // Its filter only while the Assistant is on screen.
      if (SHOW_ASSISTANT) setStatusFilter('assistant');
      handleSelect(assistant);
      return;
    }
    const closedKnown = closedConversations.find((c) => c.id === sessionId);
    if (closedKnown) {
      setStatusFilter('closed');
      handleSelect(closedKnown);
      return;
    }
    // Returning to the same link starts a new open: an earlier visit's
    // answer must not win even though its session id matches again.
    const generation = openGenerationRef.current;
    const isCurrent = () =>
      generation === openGenerationRef.current &&
      openedRef.current === sessionId &&
      history.location.pathname === supportChatPath(sessionId);
    openSession(sessionId)
      .then((conv) => {
        if (!isCurrent()) return undefined;
        if (conv.status === 'closed') setStatusFilter('closed');
        else if (SHOW_ASSISTANT && conv.handled_by === 'assistant') {
          setStatusFilter('assistant');
        }
        return handleSelect(conv);
      })
      .catch((error) => {
        if (!isCurrent()) return;
        // Not opened: the same link, followed again in this tab, tries again.
        openedRef.current = null;
        CustomToast({ type: 'error', message: errorText(error), duration: 6000 });
        history.replace(paths.support);
      });
  }, [
    sessionId,
    queueLoaded,
    conversations,
    assistantConversations,
    closedConversations,
    handleSelect,
    openSession,
    history,
  ]);

  const takeOver = async (convId: string) => {
    try {
      await writes.takeOver(convId);
      // It is a person's now, assigned to this agent: back to the queue, where it is.
      setStatusFilter('all');
      CustomToast({ type: 'success', message: 'You have this chat now' });
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not take the chat over. ${errorText(error)}`,
        duration: 6000,
      });
      throw error;
    }
  };

  /**
   * Takes a chat another agent has: ant's take over is "assign to me" on a
   * person's chat. A note in the thread then says who took it from whom;
   * the take over stands when the note does not go, and the toast says so.
   */
  const takeOverFromAgent = async (convId: string, fromName: string | null) => {
    try {
      await writes.takeOver(convId);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not take the chat over. ${errorText(error)}`,
        duration: 6000,
      });
      throw error;
    }
    try {
      await writes.sendMessage(convId, takeOverNoteText(adminName, fromName), {
        note: true,
      });
      CustomToast({ type: 'success', message: 'You have this chat now' });
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `You have this chat now, but the note could not be added. ${errorText(
          error
        )}`,
        duration: 6000,
      });
    }
  };

  /**
   * A reply or a note from the composer. A reply ant stores on an open chat
   * nobody has makes the chat this agent's (claim on first reply), with the
   * assign Assign to me uses; a note claims nothing. The claim follows the
   * reply and is not part of it: when it fails the reply is still sent, and
   * a toast says the chat is not theirs.
   */
  const sendFromComposer = async (
    conv: Conversation,
    content: string,
    options: SendOptions
  ) => {
    const claim = !options.note && isUnclaimed(conv);
    await writes.sendMessage(conv.id, content, options);
    // A colleague who took it while the reply was on its way keeps it, and
    // so does one the list has not heard of yet (claimChat asks ant): the
    // bar then says who has it.
    const now = findConversationRef.current(conv.id) ?? conv;
    if (!claim || !myAdminId || !isUnclaimed(now)) return;
    claimChat(conv.id, myAdminId).catch((error) =>
      CustomToast({
        type: 'error',
        message: `Sent, but the chat could not be assigned to you. ${errorText(error)}`,
        duration: 6000,
      })
    );
  };

  /**
   * The panel's assign. Assign to me (or picking oneself in the dialog) on a
   * chat nobody has is a claim, which asks ant first and says who has it
   * when someone does; a hand-over and Unassign go straight to ant.
   */
  const assignFromPanel = async (convId: string, adminId: string | null) => {
    const conv = findConversationRef.current(convId);
    if (adminId && adminId === myAdminId && conv && isUnclaimed(conv)) {
      const why = await claimChat(convId, adminId);
      if (why) throw new Error(why);
      return;
    }
    await writes.assign(convId, adminId);
  };

  const endConversation = async (convId: string, note: string, tags: string[]) => {
    try {
      await writes.close(convId, note, tags);
      CustomToast({ type: 'success', message: 'Conversation ended' });
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not end the conversation. ${errorText(error)}`,
        duration: 6000,
      });
      throw error;
    }
  };

  return (
    <PageContainer spaceName="Support Chat">
      <Box>
        <NotificationBanner />
        <Stack
          direction="row"
          alignItems="center"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1 }}>
            <Tab value="conversations" label="Conversations" />
            <Tab value="stats" label="Stats" />
          </Tabs>
          <Button
            component={RouterLink}
            to={paths.supportTags}
            size="small"
            startIcon={<LabelOutlinedIcon fontSize="small" />}
          >
            Tags
          </Button>
          <Button
            component={RouterLink}
            to="/support/metrics"
            size="small"
            startIcon={<InsightsOutlinedIcon fontSize="small" />}
          >
            Metrics
          </Button>
          <Button component={RouterLink} to="/support/issues" size="small">
            Issues
          </Button>
        </Stack>

        <Card
          sx={{
            display: 'flex',
            height: 'calc(100vh - 220px)',
            minHeight: 560,
            overflow: 'hidden',
          }}
        >
          {tab === 'stats' ? (
            <SupportStats />
          ) : (
            <>
              <Box
                sx={{
                  width: { xs: '100%', md: 360 },
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: 'divider',
                  display: liveSelected ? { xs: 'none', md: 'flex' } : 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <ConversationList
                  conversations={conversations}
                  closedConversations={closedConversations}
                  closedLoading={chat.closedLoading}
                  assistantConversations={assistantConversations}
                  assistantLoaded={chat.assistantLoaded}
                  statusFilter={statusFilter}
                  onStatusFilterChange={pickStatusFilter}
                  isConnected={chat.isConnected}
                  transport={chat.transport}
                  loaded={chat.queueLoaded}
                  myAdminId={chat.myAdminId}
                  selectedId={liveSelected?.id}
                  cursorId={cursorId}
                  unreadByConv={chat.unreadByConv}
                  onSelect={handleSelect}
                  onOrderChange={setOrderedIds}
                  glossary={glossary}
                />
              </Box>

              <Box
                sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
              >
                {liveSelected ? (
                  // Keyed per chat: search, its box and the end dialog start
                  // over in the next one; the drafts live in the page's store.
                  <ConversationView
                    key={liveSelected.id}
                    drafts={drafts}
                    endRequested={endRequested}
                    conversation={liveSelected}
                    messages={chat.messages[liveSelected.id]}
                    onSendMessage={(content, options) =>
                      sendFromComposer(liveSelected, content, options)
                    }
                    onEndConversation={(note, tags) =>
                      endConversation(liveSelected.id, note, tags)
                    }
                    writeRefused={!canWrite(liveSelected.id)}
                    myAdminId={chat.myAdminId}
                    adminEmail={adminEmail}
                    adminName={adminName}
                    canManageGlobalReplies={Boolean(admin?.admin?.movaticAccess)}
                    onOpenConversation={handleSelect}
                    onBack={deselect}
                    onAssign={assignFromPanel}
                    onAddTag={writes.addTag}
                    onRemoveTag={writes.removeTag}
                    onSetPriority={writes.setPriority}
                    glossary={glossary}
                    glossaryError={glossaryError}
                    onAddNote={(convId, note) =>
                      writes.sendMessage(convId, note, { note: true })
                    }
                    onTakeOver={takeOver}
                    onTakeOverFromAgent={takeOverFromAgent}
                  />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1,
                      color: 'text.secondary',
                    }}
                  >
                    <ForumOutlinedIcon sx={{ fontSize: 48, color: 'neutral.400' }} />
                    <Typography variant="subtitle1">Select a conversation</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Urgent chats first, then riders waiting longest.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Card>
      </Box>
    </PageContainer>
  );
}
